import { useEffect, useState } from "react";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sidebar } from "@/components/mai/Sidebar";
import { GmariBadge } from "@/components/mai/GmariBadge";
import { PageGuide } from "@/components/mai/Explain";
import { KpiStrip } from "@/components/mai/KpiStrip";
import { DynamicBuckets } from "@/components/mai/DynamicBuckets";
import { CountryCard } from "@/components/mai/CountryCard";
import { CountryDetail } from "@/components/mai/CountryDetail";
import { GlobalCoverage, CompareDialog } from "@/components/mai/GlobalIntel";
import { useProduct } from "@/context/ProductContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGIONS = ["APAC", "Europe", "North America", "Latin America", "Middle East", "Africa"];

export default function Dashboard() {
  const { products, productId, setProductId } = useProduct();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compare, setCompare] = useState(false);
  const [region, setRegion] = useState("");
  const [registry, setRegistry] = useState({});

  useEffect(() => {
    axios.get(`${API}/global/registry`).then((r) => {
      const map = {};
      r.data.regions.forEach((reg) => reg.markets.forEach((m) => { map[m.id] = reg.region; }));
      setRegistry(map);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/intelligence/${productId}`)
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [productId]);

  const bucketName = (id) => data?.buckets.find((b) => b.id === id)?.short_name || "";
  const visibleCountries = data ? data.countries.filter((c) => !region || registry[c.id] === region) : [];

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      <Sidebar />
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white/70 backdrop-blur-xl border-b border-slate-200/70">
          <div className="px-6 lg:px-10 py-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="overline-label text-slate-400">Global Module · Country Intelligence Engine</p>
              <h1 className="font-heading font-extrabold text-xl tracking-tight mt-0.5">Global Country Intelligence</h1>
            </div>
            <GmariBadge />
            <div className="flex items-center gap-3">
              <span className="overline-label text-slate-400 hidden sm:block">Product</span>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger data-testid="product-selector" className="w-[280px] bg-white border-slate-300 rounded-md">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`product-option-${p.id}`}>
                      {p.name} — {p.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </header>

        <main className="px-6 lg:px-10 py-8 space-y-8">
          <PageGuide pageId="intelligence" />
          {loading || !data ? (
            <div data-testid="loading-state" className="py-32 text-center text-slate-400 text-sm">Loading intelligence…</div>
          ) : (
            <>
              <section className="bg-slate-900 text-white p-8 stagger-in">
                <p className="overline-label text-slate-400">Asset Under Analysis</p>
                <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mt-2">
                  {data.product.name} <span className="text-slate-400 font-bold text-xl sm:text-2xl">· {data.product.full_name}</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 text-sm">
                  <div>
                    <p className="overline-label text-slate-500">Indication</p>
                    <p className="text-slate-200 mt-1">{data.product.indication}</p>
                  </div>
                  <div>
                    <p className="overline-label text-slate-500">Comparators</p>
                    <p className="text-slate-200 mt-1">{data.product.comparators.join(" · ")}</p>
                  </div>
                  <div>
                    <p className="overline-label text-slate-500">Clinical Maturity</p>
                    <p className="text-slate-200 mt-1">{data.product.clinical_maturity}</p>
                  </div>
                </div>
              </section>

              <GlobalCoverage onCompare={() => setCompare(true)} />

              <KpiStrip summary={data.summary} buckets={data.buckets} />

              <div className="flex items-center gap-1.5 flex-wrap">
                <button data-testid="region-filter-all" onClick={() => setRegion("")}
                  className={`text-[11px] font-semibold px-3 py-1.5 ${!region ? "bg-slate-900 text-white" : "text-slate-500 border border-slate-200 hover:bg-white"}`}>
                  All regions ({data.countries.length})
                </button>
                {REGIONS.map((r) => {
                  const n = data.countries.filter((c) => registry[c.id] === r).length;
                  return (
                    <button key={r} data-testid={`region-filter-${r.toLowerCase().replace(/ /g, "-")}`} onClick={() => setRegion(region === r ? "" : r)}
                      disabled={n === 0} title={n === 0 ? "Expansion-ready — no markets loaded yet" : ""}
                      className={`text-[11px] font-semibold px-3 py-1.5 ${region === r ? "bg-slate-900 text-white" : n === 0 ? "text-slate-300 border border-slate-100" : "text-slate-500 border border-slate-200 hover:bg-white"}`}>
                      {r} ({n})
                    </button>
                  );
                })}
              </div>

              <Tabs defaultValue="buckets">
                <TabsList className="bg-white border border-slate-200 rounded-none h-auto p-0">
                  <TabsTrigger data-testid="tab-buckets" value="buckets" className="rounded-none px-6 py-3 text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                    Dynamic Market Buckets
                  </TabsTrigger>
                  <TabsTrigger data-testid="tab-grid" value="grid" className="rounded-none px-6 py-3 text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                    Country Intelligence Grid
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="buckets" className="mt-6">
                  <DynamicBuckets countries={visibleCountries} onSelect={setSelected} />
                </TabsContent>
                <TabsContent value="grid" className="mt-6">
                  <div data-testid="country-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {visibleCountries.map((c, i) => (
                      <CountryCard key={c.id} country={c} bucketName={bucketName(c.bucket)} onSelect={setSelected} index={i} />
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </main>
      </div>
      <CountryDetail country={selected} bucketName={selected ? bucketName(selected.bucket) : ""} open={!!selected} onClose={() => setSelected(null)} />
      {compare && data && <CompareDialog countries={data.countries} onClose={() => setCompare(false)} />}
    </div>
  );
}
